<template>
  <div class="oliver-theme-picker">
    <button
      ref="btn"
      class="theme-picker-btn"
      :title="open ? 'Close theme picker' : 'Change theme'"
      @click.stop="togglePicker"
      @mousedown.stop
      @touchstart.stop
    >
      <i class="fas fa-palette"></i>
    </button>

    <transition name="theme-fade-scale">
      <div
        v-if="open"
        class="theme-picker-dropdown"
        :style="dropdownStyle"
        @click.stop
        @mousedown.stop
      >
        <div class="section-label">Color</div>
        <div class="theme-list">
          <button
            v-for="t in themes"
            :key="t.value"
            class="theme-item"
            :class="{ 'theme-item--active': current === t.value }"
            @click="selectTheme(t.value)"
          >
            <span
              class="theme-swatch"
              :style="{ background: t.gradient }"
            ></span>
            <span class="theme-name">{{ t.name }}</span>
            <i v-if="current === t.value" class="fas fa-check theme-check"></i>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { OLIVER_THEMES } from "@/components/OliverBotV2/theme/themes.js";
import { mapActions, mapState } from "vuex";

export default {
  name: "OliverThemePicker",
  data() {
    return {
      open: false,
      themes: OLIVER_THEMES,
      dropdownStyle: {},
    };
  },
  computed: {
    ...mapState({
      current: (state) => state.oliverChatBotV2.theme,
      darkMode: (state) => state.oliverChatBotV2.darkMode,
    }),
  },
  watch: {
    open(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          document.addEventListener("click", this._handleOutsideClick, true);
        });
      } else {
        document.removeEventListener("click", this._handleOutsideClick, true);
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener("click", this._handleOutsideClick, true);
  },
  methods: {
    ...mapActions("oliverChatBotV2", ["setTheme", "setDarkMode"]),

    togglePicker() {
      if (this.open) {
        this.open = false;
        return;
      }
      this.calcDropdownPos();
      this.open = true;
    },

    _handleOutsideClick(e) {
      if (!this.$el.contains(e.target)) {
        this.open = false;
      }
    },

    // backdrop-filter on an ancestor creates a containing block for position:fixed,
    // so fixed coords are relative to that ancestor, not the viewport.
    // We detect that ancestor and subtract its offset from the viewport-relative button coords.
    calcDropdownPos() {
      const btn = this.$refs.btn.getBoundingClientRect();
      const dropdownWidth = 200;
      const margin = 8;

      const cb = this._findContainingBlock();
      const cbRect = cb ? cb.getBoundingClientRect() : { top: 0, left: 0 };

      const top = btn.bottom + margin - cbRect.top;
      let left = btn.right - dropdownWidth - cbRect.left;
      if (left < margin) left = margin;
      const maxLeft = window.innerWidth - dropdownWidth - margin - cbRect.left;
      if (left > maxLeft) left = maxLeft;

      this.dropdownStyle = {
        top: `${top}px`,
        left: `${left}px`,
      };
    },

    _findContainingBlock() {
      let el = this.$el.parentElement;
      while (el && el !== document.documentElement) {
        const style = getComputedStyle(el);
        const bf = style.backdropFilter || style.webkitBackdropFilter || "";
        if (bf && bf !== "none") return el;
        if (style.transform && style.transform !== "none") return el;
        if (style.perspective && style.perspective !== "none") return el;
        el = el.parentElement;
      }
      return null;
    },

    selectTheme(value) {
      this.setTheme(value);
      this.open = false;
    },
    toggleDark() {
      this.setDarkMode(!this.darkMode);
    },
  },
};
</script>

<style lang="scss" scoped>
.oliver-theme-picker {
  position: relative;
}

.theme-picker-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--oliver-btn-subtle-bg, rgba(255, 255, 255, 0.7));
  border: 1px solid var(--oliver-accent-border, rgba(0, 0, 0, 0.06));
  color: var(--oliver-btn-subtle-color);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;

  &:hover {
    background: var(--oliver-btn-subtle-hover);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
}

.theme-picker-dropdown {
  position: fixed;
  width: 200px;
  background: var(--oliver-panel-bg, rgba(255, 255, 255, 0.97));
  border-radius: 16px;
  border: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.1));
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22);
  padding: 12px;
  z-index: 10001;
}

.section-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--oliver-text-muted, rgba(0, 0, 0, 0.4));
  padding: 4px 6px 8px;
}

.dark-mode-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 6px 12px;
  cursor: pointer;
  border-bottom: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.06));
  margin-bottom: 8px;
  user-select: none;
}

.dark-mode-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--oliver-text, #1a1a1a);

  i {
    font-size: 14px;
    color: var(--oliver-text-muted, #666);
    width: 16px;
    text-align: center;
  }
}

.toggle {
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.15);
  position: relative;
  transition: background 0.25s ease;
  flex-shrink: 0;

  &--active {
    background: var(--oliver-accent, #c2713a);
  }
}

.toggle-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  transition: transform 0.25s ease;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);

  .toggle--active & {
    transform: translateX(16px);
  }
}

.theme-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.theme-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 6px;
  border: none;
  background: transparent;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease;
  text-align: left;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  &--active {
    background: rgba(0, 0, 0, 0.05);
  }
}

.theme-swatch {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.theme-name {
  flex: 1;
  font-size: 13px;
  color: var(--oliver-text, #1a1a1a);
}

.theme-check {
  font-size: 11px;
  color: var(--oliver-accent, #c2713a);
}

.theme-fade-scale-enter-active,
.theme-fade-scale-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.theme-fade-scale-enter,
.theme-fade-scale-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-4px);
}
</style>
